<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { getApiBase } from './apiBase.js';

  const endpoint = `${getApiBase()}/studentplayground/api/admin/monthly-feedback/`;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vilnius', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  const currentMonth = Number(parts.find(p => p.type === 'year').value) * 12 + Number(parts.find(p => p.type === 'month').value) - 2;
  const lastMonth = `${Math.floor(currentMonth / 12)}-${String(currentMonth % 12 + 1).padStart(2, '0')}`;
  let month = lastMonth;
  let batch = null;
  let pages = new Map();
  let pending = new Map();
  let controllers = new Set();
  let generation = 0;
  let position = 0;
  let cacheVersion = 0;
  let logo = '';
  let busy = false;
  let navigating = false;
  let statusPending = false;
  let error = '';
  let textMode = false;
  let dialog = null;
  let holdReason = '';
  let dialogElement;
  let priorFocus;
  let poll;
  let destroyed = false;
  let searchQuery = '';
  let searchResults = null;
  let searching = false;
  let searchRequest = 0;

  $: current = (cacheVersion, pages.get(Math.floor(position / 10) * 10)?.items.find(item => item.position === position));
  $: active = !!batch?.active_job;
  $: mutable = batch?.is_current && batch?.status === 'ready' && !active && !busy;
  $: previewDocument = current ? browserDocument(current.html_body, logo) : '';

  function browserDocument(html, logoUrl) {
    const parsed = new DOMParser().parseFromString(html.replaceAll('cid:tutory_logo', logoUrl), 'text/html');
    parsed.querySelectorAll('script, iframe, object, embed, form, base, link, meta[http-equiv]').forEach(node => node.remove());
    parsed.querySelectorAll('[href], [srcset], [action]').forEach(node => {
      node.removeAttribute('href'); node.removeAttribute('srcset'); node.removeAttribute('action');
    });
    const policy = parsed.createElement('meta');
    policy.httpEquiv = 'Content-Security-Policy';
    policy.content = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'";
    parsed.head.prepend(policy);
    return '<!doctype html>' + parsed.documentElement.outerHTML;
  }

  async function api(path, method = 'GET', data = null) {
    const controller = new AbortController();
    controllers.add(controller);
    try {
      const response = await fetch(endpoint + path, {
        method, signal: controller.signal, cache: 'no-store',
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}`, 'Content-Type': 'application/json' },
        ...(data ? { body: JSON.stringify(data) } : {}),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || (response.status === 403 ? 'Superuser access required' : 'Request failed'));
      return result;
    } finally { controllers.delete(controller); }
  }

  function resetRequests() {
    generation++;
    searchRequest++; searching = false; searchResults = null;
    navigating = false;
    controllers.forEach(controller => controller.abort());
    controllers.clear(); pages = new Map(); pending = new Map(); cacheVersion++;
  }

  async function fetchPage(cursor) {
    if (pages.has(cursor)) return pages.get(cursor);
    if (pending.has(cursor)) return pending.get(cursor);
    const stamp = generation;
    const id = batch.id;
    const promise = (async () => {
      const result = await api(`batches/${id}/previews/?cursor=${cursor}`);
      if (stamp !== generation || id !== batch?.id) return null;
      if (result.batch_id !== id || result.version !== batch.version) throw new Error('This review changed. Reload before continuing.');
      pages.set(cursor, result);
      logo = result.logo_data_url;
      while (pages.size > 3) {
        const currentCursor = Math.floor(position / 10) * 10;
        const candidate = [...pages.keys()].find(key => key !== currentCursor && key !== cursor);
        pages.delete(candidate);
      }
      cacheVersion++;
      return result;
    })();
    pending.set(cursor, promise);
    try { return await promise; }
    finally { if (stamp === generation) pending.delete(cursor); }
  }

  async function loadMonth() {
    searchQuery = '';
    resetRequests(); batch = null; position = 0; dialog = null; error = ''; busy = true;
    const stamp = generation;
    try {
      const result = await api('batches/', 'POST', { month });
      if (stamp !== generation) return;
      batch = result;
      if (batch.status === 'ready') await fetchPage(0);
    } catch (err) { if (stamp === generation && err.name !== 'AbortError') error = err.message; }
    finally { if (stamp === generation) busy = false; }
  }

  async function refreshStatus() {
    if (!batch || statusPending || busy || destroyed) return;
    statusPending = true;
    const stamp = generation;
    const id = batch.id;
    try {
      const result = await api(`batches/${id}/`);
      if (stamp !== generation || busy) return;
      if (result.status === 'ready' && !result.is_current && result.current_batch_id) {
        batch = await api(`batches/${result.current_batch_id}/`);
        resetRequests(); position = 0;
        await fetchPage(0);
      } else {
        const changed = batch.version !== result.version || batch.status !== result.status
          || JSON.stringify(batch.counts) !== JSON.stringify(result.counts);
        batch = result;
        if (changed && batch.status === 'ready') {
          resetRequests();
          position = Math.min(position, Math.max(0, batch.total - 1));
          await fetchPage(Math.floor(position / 10) * 10);
        }
      }
    } catch (err) { if (stamp === generation && err.name !== 'AbortError') error = err.message; }
    finally { statusPending = false; }
  }

  async function navigate(offset) {
    if (dialog || navigating || !batch || position + offset < 0 || position + offset >= batch.total) return;
    const stamp = generation;
    navigating = true; error = '';
    try {
      const target = position + offset;
      const result = await fetchPage(Math.floor(target / 10) * 10);
      if (!result || stamp !== generation) return;
      position = target;
      // Two cached emails remain ahead: load only the next page, once.
      if (result.next_cursor !== null && result.next_cursor - position <= 3) {
        fetchPage(result.next_cursor).catch(err => { if (stamp === generation && err.name !== 'AbortError') error = err.message; });
      }
    } catch (err) { if (stamp === generation && err.name !== 'AbortError') error = err.message; }
    finally { if (stamp === generation) navigating = false; }
  }

  async function retryPreview() {
    error = '';
    try { await fetchPage(Math.floor(position / 10) * 10 + (current ? 10 : 0)); }
    catch (err) { if (err.name !== 'AbortError') error = err.message; }
  }

  async function searchRecipients() {
    const query = searchQuery.trim();
    const request = ++searchRequest;
    const stamp = generation;
    searchResults = null; error = '';
    if (!query || !batch) return;
    searching = true;
    try {
      const result = await api(`batches/${batch.id}/recipients/?q=${encodeURIComponent(query)}`);
      if (request !== searchRequest || stamp !== generation) return;
      if (result.batch_id !== batch.id || result.version !== batch.version) throw new Error('This review changed. Search again.');
      searchResults = result;
    } catch (err) { if (request === searchRequest && stamp === generation && err.name !== 'AbortError') error = err.message; }
    finally { if (request === searchRequest && stamp === generation) searching = false; }
  }

  async function selectRecipient(result) {
    if (dialog || navigating || busy) return;
    await navigate(result.position - position);
    searchResults = null;
  }

  async function openDialog(mode) {
    priorFocus = document.activeElement;
    holdReason = '';
    dialog = { mode, batchId: batch.id, version: batch.version, itemId: current?.id, month: batch.month,
      recipientEmail: current?.recipient_email, studentNames: current?.students.map(student => student.name).join(', '),
      familyCount: batch.counts[mode === 'retry' ? 'failed' : 'pending'],
      studentCount: batch.student_counts[mode === 'retry' ? 'failed' : 'pending'] || 0,
      heldCount: batch.counts.held };
    await tick();
    dialogElement?.querySelector('textarea, button')?.focus();
  }

  function closeDialog() { dialog = null; priorFocus?.focus(); }

  async function mutate(mode, confirmation = dialog) {
    if (busy) return;
    busy = true; error = '';
    const stamp = generation;
    try {
      const expected_revision = confirmation?.version ?? batch.version;
      let result;
      if (mode === 'hold' || mode === 'restore') {
        result = await api(`items/${confirmation?.itemId ?? current.id}/`, 'PATCH',
          { held: mode === 'hold', reason: holdReason.trim(), expected_revision });
      } else {
        const single = mode === 'send-one' || mode === 'retry-one';
        const action = mode === 'retry' || mode === 'retry-one' ? 'retry-failed' : mode === 'send-one' ? 'send' : mode;
        result = await api(`batches/${confirmation?.batchId ?? batch.id}/${action}/`, 'POST',
          { expected_revision, ...(single ? { item_id: confirmation.itemId } : {}) });
      }
      if (stamp !== generation) return;
      batch = result; busy = false; closeDialog(); resetRequests();
      if (mode === 'rebuild') position = 0;
      if (batch.status === 'ready') await fetchPage(Math.floor(position / 10) * 10);
    } catch (err) { if (stamp === generation && err.name !== 'AbortError') { closeDialog(); error = err.message; } }
    finally { if (stamp === generation) busy = false; }
  }

  function keydown(event) {
    if (dialog) {
      if (event.key === 'Escape' && !busy) { event.preventDefault(); closeDialog(); }
      if (event.key === 'Tab') {
        const focusable = [...dialogElement.querySelectorAll('button:not(:disabled), textarea')];
        const next = event.shiftKey ? focusable[0] : focusable.at(-1);
        if (document.activeElement === next) { event.preventDefault(); (event.shiftKey ? focusable.at(-1) : focusable[0]).focus(); }
      }
      return;
    }
    if (event.target.closest('input, textarea, select, [contenteditable="true"]') || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); navigate(event.key === 'ArrowRight' ? 1 : -1); }
  }

  onMount(() => { loadMonth(); poll = setInterval(refreshStatus, 5000); });
  onDestroy(() => { destroyed = true; clearInterval(poll); resetRequests(); });
</script>

<svelte:window on:keydown={keydown} />

<main class="review">
  <header>
    <div><h1>Monthly feedback review</h1><p>Review the frozen email each family would receive. Navigation does not approve or send email.</p></div>
    <div class="month-controls">
      <label>Reporting month <input aria-label="Reporting month" type="month" max={lastMonth} bind:value={month} on:change={loadMonth} /></label>
    </div>
  </header>
  {#if error}<div class="error" role="alert">{error} <button on:click={retryPreview} disabled={!batch || batch.status !== 'ready'}>Retry preview loading</button></div>{/if}
  {#if !batch}<p role="status">{busy ? 'Loading review…' : 'Select a completed month to load its review.'}</p>
  {:else}
    {#if batch.status === 'ready' && batch.total > 0}
      <section class="recipient-search" aria-label="Find a recipient">
        <form on:submit|preventDefault={searchRecipients}>
          <label for="recipient-search">Find a person</label>
          <div class="search-controls">
            <input id="recipient-search" type="search" placeholder="Parent name, student name or email" maxlength="254" bind:value={searchQuery}
              on:input={() => { searchRequest++; searching = false; searchResults = null; }} disabled={!!dialog || busy} />
            <button type="submit" disabled={searching || !searchQuery.trim() || !!dialog || busy}>Search</button>
          </div>
        </form>
        {#if searching}<p role="status">Searching recipients…</p>{/if}
        {#if searchResults}
          {#if searchResults.items.length === 0}<p role="status">No matching recipients in this month.</p>
          {:else}
            <ul class="search-results">
              {#each searchResults.items as result}
                <li><button on:click={() => selectRecipient(result)} disabled={navigating || !!dialog || busy}>
                  <strong>{result.recipient_email}</strong>
                  <span>{result.parent_names.join(', ')}</span>
                  <span>{result.students.map(student => student.name).join(', ')}</span>
                </button></li>
              {/each}
            </ul>
            {#if searchResults.truncated}<p role="status">Showing the first 20 matches. Refine your search to find someone else.</p>{/if}
          {/if}
        {/if}
      </section>
    {/if}
    {#if batch.status === 'ready' && batch.total === 0}<p role="status">No eligible teacher feedback emails for this month.</p>
    {:else if current}
      <section class="sending-details" aria-label="Sending details">
        <dl><dt>To</dt><dd>{current.recipient_email}</dd><dt>Students</dt><dd>{current.students.map(student => student.name).join(', ')}</dd><dt>Subject</dt><dd>{current.subject}</dd><dt>Status</dt><dd>{current.state === 'delivery_unknown' ? 'Unresolved — do not retry without investigating delivery' : current.state}</dd></dl>
        {#if current.held}<p class="hold-reason">{current.hold_reason}</p>{/if}
      </section>
      <section class="email-card" aria-label="Family email">
        <div class="preview-controls">
          <div class="preview-toolbar">
            <button aria-label="Previous email" on:click={() => navigate(-1)} disabled={position === 0 || navigating || !!dialog}>← Previous</button>
            <span aria-live="polite">Email {position + 1} of {batch.total}</span>
            <button aria-label="Next email" on:click={() => navigate(1)} disabled={position + 1 >= batch.total || navigating || !!dialog}>Next →</button>
          </div>
          <div class="email-actions">
            {#if current.state === 'failed'}
              <button class="primary" on:click={() => openDialog('retry-one')} disabled={!mutable || !batch.sending_enabled || current.held}>Retry this email</button>
            {:else}
              <button class="primary" on:click={() => openDialog('send-one')} disabled={!mutable || !batch.sending_enabled || current.held || current.state !== 'pending'}>Send this email</button>
            {/if}
            <button on:click={() => textMode = !textMode}>{textMode ? 'HTML preview' : 'Plain text'}</button>
            {#if current.held}<button on:click={() => mutate('restore')} disabled={!mutable}>Restore family email</button>
            {:else}<button on:click={() => openDialog('hold')} disabled={!mutable || !['pending', 'failed'].includes(current.state)}>Hold family email</button>{/if}
          </div>
        </div>
        {#if textMode}<pre>{current.text_body}</pre>
        {:else}<iframe title="Email preview" sandbox="" referrerpolicy="no-referrer" srcdoc={previewDocument}></iframe>{/if}
      </section>
    {:else}<p role="status">{batch.status === 'ready' ? 'Loading previews…' : 'Preparing frozen feedback and the first ten previews…'}</p>{/if}
    <section class="batch-info" aria-label="Batch status">
      <p><strong>{batch.month} · Revision {batch.revision}</strong> · {batch.status}
        {#if batch.prepared_at} · Frozen {new Date(batch.prepared_at).toLocaleString('lt-LT', { timeZone: 'Europe/Vilnius' })}{/if}</p>
      <div class="counts">{#each ['sent', 'held', 'failed', 'delivery_unknown', 'skipped'] as state}<span>{state === 'delivery_unknown' ? 'Unresolved' : state[0].toUpperCase() + state.slice(1)}: {batch.counts[state] || 0}</span>{/each}<span>Eligible: {batch.counts.pending}</span></div>
      {#if batch.active_job}<p role="status">{batch.active_job.operation}: {batch.active_job.processed} / {batch.active_job.total || 'preparing'} · {batch.active_job.status}. You may close this page; work continues.</p>{/if}
      {#if batch.last_job?.status === 'failed'}<p class="error">Operation failed: {batch.last_job.error}. The last good revision remains available.</p>{/if}
      {#if !batch.sending_enabled}<p>SMTP delivery is disabled. Preparation and review remain available.</p>{/if}
      {#if batch.summary.already_covered_students}<p>Already delivered or unresolved student sections excluded: {batch.summary.already_covered_students}</p>{/if}
      {#if Object.keys(batch.summary.skipped || {}).length}<details><summary>Selection exclusions</summary>{#each Object.entries(batch.summary.skipped) as [reason, count]}<p>{reason.replaceAll('_', ' ')}: {count}</p>{/each}</details>{/if}
      <button on:click={() => openDialog('rebuild')} disabled={!mutable}>Rebuild unsent emails</button>
    </section>
    <footer>
      <p>Held, sent, failed and unresolved emails are excluded from normal delivery. Definite failures require a separate retry.</p>
      <div class="footer-actions">
        <button class="primary" on:click={() => openDialog('send')} disabled={!mutable || !batch.sending_enabled || batch.counts.pending === 0}>{batch.counts.sent > 0 ? 'Send remaining emails' : 'Send all eligible emails'}</button>
        <button on:click={() => openDialog('retry')} disabled={!mutable || !batch.sending_enabled || batch.counts.failed === 0}>Retry definite failures</button>
      </div>
    </footer>
  {/if}
</main>

{#if dialog}
  <div class="modal-backdrop">
    <section role="dialog" aria-modal="true" aria-labelledby="review-dialog-title" bind:this={dialogElement} class="modal">
      <h2 id="review-dialog-title">{dialog.mode === 'send-one' ? 'Send this email' : dialog.mode === 'retry-one' ? 'Retry this email' : dialog.mode === 'hold' ? 'Hold this family email' : dialog.mode === 'rebuild' ? 'Rebuild unsent emails' : dialog.mode === 'retry' ? 'Retry definite failures' : 'Send eligible emails'}</h2>
      {#if dialog.mode === 'hold'}
        <label>Hold reason <textarea aria-label="Hold reason" maxlength="2000" bind:value={holdReason}></textarea></label>
        <p>The entire family email will be held. Original teacher feedback is not edited.</p>
      {:else if dialog.mode === 'send-one' || dialog.mode === 'retry-one'}
        <p>Send the frozen feedback for {dialog.month} to <strong>{dialog.recipientEmail}</strong>?</p>
        <p>Students: {dialog.studentNames}</p>
        <p>Only this recipient will receive an email.</p>
      {:else if dialog.mode === 'rebuild'}
        <p>Create a new frozen revision from current feedback and parent details for {dialog.month}? Holds carry over. Sent and unresolved student sections will not be resent.</p>
      {:else}
        <p>{dialog.month} · Revision {batch.revision}</p>
        <p>{dialog.familyCount} family emails · {dialog.studentCount} students</p>
        <p>Held family emails excluded: {dialog.heldCount}</p>
        <p>{dialog.mode === 'retry' ? 'Only definite failures are retried. Unresolved deliveries are excluded.' : 'Emails you have not opened are included. This sends the frozen reviewed version, not later edits.'}</p>
      {/if}
      <div class="footer-actions"><button on:click={closeDialog} disabled={busy}>Cancel</button><button class="primary" disabled={busy || (dialog.mode === 'hold' && !holdReason.trim())} on:click={() => mutate(dialog.mode)}>{dialog.mode === 'hold' ? 'Save hold' : dialog.mode === 'rebuild' ? 'Confirm rebuild' : dialog.mode === 'retry' ? 'Confirm retry' : 'Confirm send'}</button></div>
    </section>
  </div>
{/if}

<style>
  .recipient-search { margin: 20px 0; }
  .recipient-search label { display: block; margin-bottom: 8px; font-weight: bold; }
  .search-controls { display: flex; gap: 8px; }
  .search-controls input { flex: 1; min-width: 0; padding: 10px; border: 1px solid #bbb; border-radius: 6px; }
  .search-results { list-style: none; padding: 0; max-height: 300px; overflow-y: auto; }
  .search-results button { width: 100%; text-align: left; margin-bottom: 6px; overflow-wrap: anywhere; }
  .search-results span { display: block; font-size: 13px; margin-top: 3px; }
  .review { max-width: 1100px; margin: 0 auto; padding: 24px; color: #292929; font-family: Arial, sans-serif; }
  h1 { font-size: 28px; margin: 0 0 12px; } p { line-height: 1.5; }
  header, .month-controls, .preview-toolbar, .email-actions, .footer-actions, .counts { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  header { justify-content: space-between; margin-bottom: 24px; }
  label { display: flex; flex-direction: column; gap: 8px; }
  button, input, textarea { font: inherit; padding: 12px 16px; border: 1px solid #bfb7aa; border-radius: 8px; background: #fff; color: #292929; }
  button { cursor: pointer; min-height: 44px; } button:hover:not(:disabled) { background: #fff4e4; } button:disabled { opacity: .5; cursor: not-allowed; }
  button:focus-visible, input:focus-visible, textarea:focus-visible { outline: 3px solid #a85800; outline-offset: 3px; }
  .primary { background: #fab45a; border-color: #e99c34; font-weight: bold; }
  .batch-info, .email-card, .sending-details { background: #fff; border: 1px solid #eadac4; border-top: 4px solid #fab45a; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
  .batch-info { background: #fff8ee; } .counts span { background: #fff; padding: 8px 12px; border-radius: 8px; }
  .preview-controls { position: sticky; top: 12px; z-index: 10; background: #fff8ee; border: 1px solid #eadac4; border-radius: 10px; padding: 12px; margin-bottom: 16px; box-shadow: 0 4px 12px #29292912; }
  .preview-toolbar { justify-content: space-between; } dl { display: grid; grid-template-columns: 80px minmax(0, 1fr); gap: 10px; } dt { font-weight: bold; } dd { margin: 0; overflow-wrap: anywhere; }
  iframe { display: block; box-sizing: border-box; width: 100%; height: 720px; border: 1px solid #eadac4; background: #fff; border-radius: 8px; }
  pre { white-space: pre-wrap; overflow-wrap: anywhere; padding: 16px; background: #fff8ee; }
  .error { background: #ffe9e9; color: #7f1616; padding: 12px; border-radius: 8px; margin: 12px 0; }
  .hold-reason { background: #fff0d8; padding: 12px; border-left: 4px solid #a85800; }
  footer { padding: 16px 0 32px; } .modal-backdrop { position: fixed; inset: 0; background: #0008; display: flex; justify-content: center; align-items: center; z-index: 10000; padding: 20px; }
  .modal { background: white; border-top: 5px solid #fab45a; border-radius: 12px; padding: 24px; max-width: 560px; max-height: 90vh; overflow-y: auto; width: 100%; } textarea { min-height: 100px; }
  @media (max-width: 600px) { .review { padding: 12px; } .email-card, .sending-details, .batch-info { padding: 12px; } .preview-controls { top: 8px; padding: 8px; } h1 { font-size: 24px; } .preview-toolbar { gap: 6px; } .preview-toolbar button { padding: 10px; } .modal { padding: 16px; } }
</style>
